import React, { FunctionComponent, useState, MouseEvent } from "react";
import { styled } from "@iventis/styles";
import { InteractiveElement } from "@iventis/styles/src/components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DisabledOverlay } from "./disabled-overlay";
import { ItemOption, ItemOptionsComponent } from "./sidebar-item-options";
import ItemNameComponent from "./sidebar-item-name";

export interface SidebarItem {
    id: string;
    name: string;
    selected: boolean;
    /** Is the item the only item selected */
    singleSelection: boolean;
    editName: boolean;
    visible?: boolean;
    allowEditName: boolean;
    itemOptions?: ItemOption[];
    pending?: boolean;
    className?: string;
    itemHeight?: string;
    dataCy?: string;
    onClick?: (id: string, event?: MouseEvent<HTMLElement>) => void;
    nameChange?: (id: string, newName: string) => void;
    onItemNameChange?: (id: string, newName: string) => void;
    changeEditName?: (id: string, value: boolean) => void;
    blockInteractivity?: boolean;
}

/**
 * A basic component which displays a sidebar item
 * @param {string} id - id of the item
 * @param {string} name - name of the item
 * @param {boolean} selected - if the item is selected or not
 * @param {boolean} editName - if the name is being edited
 * @param {boolean} allowEditName - if you are allowed to edit the name
 * @param {ItemOption[]} itemOptions - an array of item options to display when the item is hovered over (optional)
 * @param {(id: string, newName: string) => void} onItemNameChange - function to handle the name being changed
 * @param {(id: string) => void} onClick - click event function that passes the item's id
 * @param {(value: boolean) => void;} changeEditName - emit new boolean value when name is being changed
 */
export const SidebarItemComponent: FunctionComponent<SidebarItem & { onInvalidNameInputted: () => void }> = ({
    id,
    name,
    selected,
    singleSelection,
    itemOptions = [],
    editName,
    allowEditName,
    className,
    pending,
    itemHeight,
    onItemNameChange,
    changeEditName,
    onClick,
    dataCy,
    visible,
    blockInteractivity = false,
    onInvalidNameInputted,
}) => {
    const [show, setShow] = useState(false);

    const handleChangeName = (newName: string, id: string) => {
        if (newName?.length >= 2) {
            onItemNameChange(id, newName);
        } else {
            onInvalidNameInputted();
            changeEditName(id, false);
        }
    };

    return (
        <SidebarItemContainer
            selected={selected}
            onMouseEnter={() => !pending && setShow(true)}
            onMouseLeave={() => !pending && setShow(false)}
            style={{ ...(pending ? { pointerEvents: "none" } : undefined) }}
            itemHeight={itemHeight}
        >
            {pending && <DisabledOverlay />}
            <ItemInteractiveElement
                data-cy={dataCy}
                onClick={(event) => {
                    if (onClick && !editName) onClick(id, event);
                }}
                disabled={blockInteractivity}
            >
                {pending && (
                    <div>
                        <FontAwesomeIcon style={{ alignSelf: "center", width: "20px" }} icon="circle-notch" spin />
                    </div>
                )}

                <ItemNameComponent
                    name={name}
                    visible={visible}
                    // Only allow single click edit if it is the only selected item
                    allowSingleClickEdit={selected && singleSelection}
                    editName={editName}
                    setEditName={(value) => changeEditName(id, value)}
                    changeItemName={(newName) => handleChangeName(newName, id)}
                    allowEditName={allowEditName}
                    className={className}
                />
            </ItemInteractiveElement>
            <ItemOptionsComponent itemOptions={itemOptions} itemId={id} show={selected || show} />
        </SidebarItemContainer>
    );
};

const SidebarItemContainer = styled.div<{ selected: boolean; itemHeight: string }>`
    position: relative;
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: ${({ itemHeight }) => itemHeight};
    overflow: hidden;
    flex: 1 1;
    cursor: pointer;
    padding-left: 10px;
    min-width: 0;
`;

const ItemInteractiveElement = styled(InteractiveElement)`
    display: flex;
    height: 100%;
    align-items: center;
    flex: 1 1;
    min-width: 0;
`;
